import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Pencil, Search, Wrench, ToggleLeft, ToggleRight } from 'lucide-react';
import Modal from '../components/ui/Modal';
import { getTools, createTool, updateTool } from '../api/tools';

const EMPTY_FORM = { name: '', monthly_price: '' };

const ToolFormModal = ({ isOpen, onClose, onSaved, editTool }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setForm(editTool ? { name: editTool.name, monthly_price: editTool.monthly_price } : EMPTY_FORM);
      setErrors({});
    }
  }, [isOpen, editTool]);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Tool name is required.';
    if (form.monthly_price === '' || isNaN(form.monthly_price)) errs.monthly_price = 'Enter a valid price.';
    else if (Number(form.monthly_price) < 0) errs.monthly_price = 'Price cannot be negative.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setLoading(true);
    try {
      const payload = { name: form.name.trim(), monthly_price: Number(form.monthly_price) };
      if (editTool) {
        const res = await updateTool(editTool.id, payload);
        const synced = res.data?.synced_allocations || 0;
        const priceChanged = Number(form.monthly_price) !== Number(editTool.monthly_price);
        if (priceChanged && synced > 0) {
          toast.success(`Tool updated. Monthly charge synced for ${synced} active employee${synced !== 1 ? 's' : ''}.`);
        } else if (priceChanged && synced === 0) {
          toast.success('Tool updated. No active employee allocations to sync.');
        } else {
          toast.success('Tool updated successfully.');
        }
      } else {
        await createTool(payload);
        toast.success('Tool added successfully.');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={editTool ? 'Edit AI Tool' : 'Add AI Tool'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Tool Name</label>
          <input
            className="input"
            placeholder="e.g. GitHub Copilot"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
        </div>
        <div>
          <label className="label">Monthly Price (USD)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
            <input
              className="input pl-7"
              type="number"
              min="0"
              step="0.01"
              placeholder="19.00"
              value={form.monthly_price}
              onChange={(e) => setForm({ ...form, monthly_price: e.target.value })}
            />
          </div>
          {errors.monthly_price && <p className="text-xs text-red-500 mt-1">{errors.monthly_price}</p>}
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button type="submit" className="btn-primary flex-1" disabled={loading}>
            {loading ? 'Saving...' : editTool ? 'Save Changes' : 'Add Tool'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const Tools = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTool, setEditTool] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const fetchTools = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTools();
      setTools(res.data);
    } catch {
      toast.error('Failed to load AI tools.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTools(); }, [fetchTools]);

  const handleToggleActive = async (tool) => {
    setTogglingId(tool.id);
    try {
      await updateTool(tool.id, { is_active: !tool.is_active });
      toast.success(`Tool ${tool.is_active ? 'deactivated' : 'activated'}.`);
      fetchTools();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status.');
    } finally {
      setTogglingId(null);
    }
  };

  const openAdd = () => { setEditTool(null); setModalOpen(true); };
  const openEdit = (tool) => { setEditTool(tool); setModalOpen(true); };

  const filtered = tools.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Tools</h1>
          <p className="text-sm text-gray-500 mt-1">Manage the AI tools available for allocation.</p>
        </div>
        <button onClick={openAdd} className="btn-primary">
          <Plus className="w-4 h-4" />
          Add Tool
        </button>
      </div>

      {/* Card */}
      <div className="card">
        {/* Search bar */}
        <div className="p-4 border-b border-gray-100">
          <div className="relative max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              className="input pl-9"
              placeholder="Search tools..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-6 py-3 font-medium text-gray-500">#</th>
                <th className="text-left px-6 py-3 font-medium text-gray-500">Tool Name</th>
                <th className="text-left px-6 py-3 font-medium text-gray-500">Monthly Price</th>
                <th className="text-left px-6 py-3 font-medium text-gray-500">Status</th>
                <th className="text-left px-6 py-3 font-medium text-gray-500">Added On</th>
                <th className="text-right px-6 py-3 font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                    <div className="flex flex-col items-center gap-2">
                      <svg className="animate-spin h-6 w-6 text-primary-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      <span>Loading tools...</span>
                    </div>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-400">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                        <Wrench className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-500">No tools found</p>
                        <p className="text-xs mt-0.5">
                          {search ? `No results for "${search}"` : 'Click "Add Tool" to get started.'}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((tool, idx) => (
                  <tr key={tool.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-gray-400">{idx + 1}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Wrench className="w-4 h-4 text-primary-600" />
                        </div>
                        <span className="font-medium text-gray-900">{tool.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      ${Number(tool.monthly_price).toFixed(2)}
                      <span className="text-gray-400 font-normal text-xs ml-1">/mo</span>
                    </td>
                    <td className="px-6 py-4">
                      {tool.is_active ? (
                        <span className="badge-active">Active</span>
                      ) : (
                        <span className="badge-expired">Inactive</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {new Date(tool.created_at).toLocaleDateString('en-US', {
                        year: 'numeric', month: 'short', day: 'numeric',
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(tool)}
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(tool)}
                          disabled={togglingId === tool.id}
                          className={`p-1.5 rounded-lg transition-colors ${
                            tool.is_active
                              ? 'text-green-500 hover:text-red-500 hover:bg-red-50'
                              : 'text-gray-400 hover:text-green-500 hover:bg-green-50'
                          }`}
                          title={tool.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {tool.is_active
                            ? <ToggleRight className="w-5 h-5" />
                            : <ToggleLeft className="w-5 h-5" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer count */}
        {!loading && filtered.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-100 text-xs text-gray-400">
            Showing {filtered.length} of {tools.length} tool{tools.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      <ToolFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={fetchTools}
        editTool={editTool}
      />
    </div>
  );
};

export default Tools;
